/**
 * Belegungsverlauf — jeder Aufenthalt des Zeitraums als Streifen in der Farbe seines Kanals.
 *
 * <p>Eine Übersichtsgrafik, keine Bedienfläche: ein Aufenthalt von zwei Nächten in einem Jahr ist
 * ein Streifen von wenigen Pixeln und wäre auch mit Tastaturfokus kein sinnvolles Ziel. Die
 * Einzelheiten stehen deshalb vollständig im Reiter „Buchungen" darunter; hier trägt die Grafik
 * eine Textbeschreibung und die Farben stehen in der Legende des Kanalmix.</p>
 */
import { useMemo } from 'react';
import { Card, CardBody, Eyebrow } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { formatDate, formatNumber } from '@/shared/lib/format';
import type { BookingResponse } from '@/shared/api';
import { channelColor, channelLabel, CHART_COLORS } from './financeChartTheme';
import {
  channelMatches,
  computeTodayPercent,
  daysBetween,
  monthLabelsBetween,
  toSegment,
  type StreamSegment,
} from './stayStreamMath';

interface Props {
  bookings: BookingResponse[];
  periodFrom: string;
  periodTo: string;
  highlightChannel?: string | null;
}

export function StayStream({ bookings, periodFrom, periodTo, highlightChannel }: Props) {
  // Date-Objekte memoisieren, damit die Dep-Arrays stabil bleiben.
  const from = useMemo(() => new Date(periodFrom), [periodFrom]);
  const to = useMemo(() => new Date(periodTo), [periodTo]);
  const totalDays = Math.max(1, daysBetween(from, to) + 1);

  const segments = useMemo(
    () => bookings.map((booking) => toSegment(booking, from, totalDays)),
    [bookings, from, totalDays],
  );
  const monthLabels = useMemo(() => monthLabelsBetween(from, to), [from, to]);
  const todayPercent = useMemo(
    () => computeTodayPercent(from, to, totalDays),
    [from, to, totalDays],
  );

  return (
    <Card>
      <CardBody className="p-4 sm:p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Eyebrow variant="muted">Belegungsverlauf</Eyebrow>
          <span className="text-ink-soft text-xs">
            {formatNumber(bookings.length)} {bookings.length === 1 ? 'Aufenthalt' : 'Aufenthalte'} ·{' '}
            {formatDate(periodFrom)} – {formatDate(periodTo)}
          </span>
        </div>

        {segments.length === 0 ? (
          <p className="text-ink-soft py-8 text-center text-sm">
            In diesem Zeitraum liegt kein Aufenthalt.
          </p>
        ) : (
          <>
            <div
              role="img"
              aria-label={`Belegung von ${formatDate(periodFrom)} bis ${formatDate(periodTo)}: ${formatNumber(bookings.length)} Aufenthalte. Die Einzelheiten stehen im Reiter Buchungen.`}
              className="relative mt-6 h-12 w-full"
            >
              <div className="bg-card-alt/60 absolute inset-0 rounded" />
              {segments.map((segment) => (
                <StreamSegmentChip
                  key={segment.id}
                  segment={segment}
                  dimmed={
                    highlightChannel != null && !channelMatches(segment.channel, highlightChannel)
                  }
                />
              ))}
              {todayPercent !== null && (
                <div
                  className="bg-navy absolute bottom-0 top-0 w-px"
                  style={{ left: `${todayPercent}%` }}
                  title="Heute"
                />
              )}
            </div>

            <div
              aria-hidden
              className="text-ink-faint relative mt-2 grid w-full text-[10px] uppercase tracking-wider"
              style={{ gridTemplateColumns: `repeat(${monthLabels.length}, minmax(0, 1fr))` }}
            >
              {monthLabels.map((label) => (
                <span key={label} className="truncate text-center">
                  {label}
                </span>
              ))}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function StreamSegmentChip({ segment, dimmed }: { segment: StreamSegment; dimmed: boolean }) {
  const cancelled = segment.status === 'Cancelled' || segment.status === 'NoShow';
  return (
    <div
      title={`${channelLabel(segment.channel)} · ${formatDate(segment.checkIn)} – ${formatDate(segment.checkOut)} · ${segment.priceLabel}${cancelled ? ' · storniert' : ''}`}
      className={cn(
        'absolute bottom-1 top-1 rounded transition-opacity duration-150 motion-reduce:transition-none',
        dimmed && 'opacity-15',
        cancelled && 'opacity-40',
      )}
      style={{
        left: `${segment.leftPercent}%`,
        width: `${segment.widthPercent}%`,
        background: cancelled
          ? `repeating-linear-gradient(45deg, ${CHART_COLORS.cancelled} 0 4px, transparent 4px 8px)`
          : channelColor(segment.channel),
      }}
    />
  );
}
